import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";
import { cachedRequest } from "../../../utils/requestCache";

export interface LifecycleType {
  lifecycle_type_id: number;
  lifecycle_type_key: string;
  lifecycle_type_display_name: string;
  is_active: boolean | null;
  tenant_id: number;
}

export interface LifecycleStage {
  lifecycle_type_stage_id: number;
  stage_name: string;
  stage_order: number;
  tenant_id: number;
  lifecycle_type_id: number;
}

export const LifecycleService = {
  getLifecycleTypes: async (tenantId: number): Promise<LifecycleType[]> => {
    try {
      return await cachedRequest(`lifecycle-types:${tenantId}`, async () => {
        const response = await axios.get(
          `${API_BASE_URL}/lifecycle-type/?tenant_id=${tenantId}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (response.data.results) {
          return response.data.results;
        } else if (Array.isArray(response.data)) {
          return response.data;
        }

        return [];
      });
    } catch (error) {
      console.error("Error fetching lifecycle types:", error);
      return handleAuthError(error);
    }
  },

  getLifecycleStages: async (
    lifecycleTypeId: number,
    tenantId: number
  ): Promise<LifecycleStage[]> => {
    try {
      return await cachedRequest(`lifecycle-stages:${lifecycleTypeId}:${tenantId}`, async () => {
        const response = await axios.get(
          `${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${lifecycleTypeId}&tenant_id=${tenantId}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (Array.isArray(response.data)) {
          return response.data.sort(
            (a: LifecycleStage, b: LifecycleStage) =>
              a.stage_order - b.stage_order
          );
        }

        return [];
      });
    } catch (error) {
      console.error("Error fetching lifecycle stages:", error);
      return handleAuthError(error);
    }
  },

  getLeadStages: async (tenantId: number): Promise<LifecycleStage[]> => {
    try {
      const lifecycleTypes = await LifecycleService.getLifecycleTypes(tenantId);

      const leadLifecycleType = lifecycleTypes.find(
        (type) => type.lifecycle_type_key === "LEAD"
      );

      if (!leadLifecycleType) {
        console.warn("No LEAD lifecycle type found for tenant:", tenantId);
        return [];
      }

      return await LifecycleService.getLifecycleStages(
        leadLifecycleType.lifecycle_type_id,
        tenantId
      );
    } catch (error) {
      console.error("Error fetching lead stages:", error);
      return handleAuthError(error);
    }
  },
};
